import { createSlice } from '@reduxjs/toolkit';
import { EDfeType, ICTe } from '../common/cte.interface';

export interface NewContractProposalState {
  availableCtes: {
    items: ICTe[];
    selected: ICTe[];
  };
  // Holds titles with due date (NF-e XML + CT-e/NF-e API). Not NFE-only after SUS-97.
  availableNfes: {
    items: ICTe[];
    selected: ICTe[];
  };
  proposalCtes: {
    items: ICTe[];
    selected: ICTe[];
  };
  // Mirrors availableNfes: due-date titles selected for the proposal.
  proposalNfes: {
    items: ICTe[];
    selected: ICTe[];
  };
}

// Type-safe property mapping
type DfePropertyKeys = {
  available: keyof NewContractProposalState;
  proposal: keyof NewContractProposalState;
};

const dfePropertyMap: Record<EDfeType, DfePropertyKeys> = {
  [EDfeType.CTE]: {
    available: 'availableCtes',
    proposal: 'proposalCtes',
  },
  [EDfeType.NFE]: {
    available: 'availableNfes',
    proposal: 'proposalNfes',
  },
} as const;

const initialState: NewContractProposalState = {
  availableCtes: {
    items: [],
    selected: [],
  },
  proposalCtes: {
    items: [],
    selected: [],
  },
  availableNfes: {
    items: [],
    selected: [],
  },
  proposalNfes: {
    items: [],
    selected: [],
  },
};

const contractSlice = createSlice({
  name: 'newContractProposal',
  initialState,
  reducers: {
    reset: () => {
      return { ...initialState };
    },
    setAvailableDfes: (state: NewContractProposalState, action: { payload: { items: ICTe[]; dfeType: EDfeType } }) => {
      const { items, dfeType } = action.payload;
      const { available, proposal } = dfePropertyMap[dfeType];

      state[available].items = items.filter((item) => !state[proposal].items.some(({ id }) => id === item.id));
    },
    deleteDfes: (state: NewContractProposalState, action: { payload: { items: ICTe[]; dfeType: EDfeType } }) => {
      const { items, dfeType } = action.payload;
      const { available } = dfePropertyMap[dfeType];

      state[available].items = state[available].items.filter((item) => !items.some(({ id }) => id === item.id));
      state[available].selected = [];
    },
    addDfeToProposal: (state: NewContractProposalState, action: { payload: { dfeType: EDfeType } }) => {
      const { dfeType } = action.payload;
      const { available, proposal } = dfePropertyMap[dfeType];

      state[proposal].items = [...state[proposal].items, ...state[available].selected];
      state[available].items = state[available].items.filter(
        (item) => !state[available].selected.some((selected) => selected.id === item.id)
      );
      state[available].selected = [];
    },
    removeDfeFromProposal: (state: NewContractProposalState, action: { payload: { dfeType: EDfeType } }) => {
      const { dfeType } = action.payload;
      const { available, proposal } = dfePropertyMap[dfeType];

      state[available].items = [...state[available].items, ...state[proposal].selected];
      state[proposal].items = state[proposal].items.filter((item) => !state[proposal].selected.some((selected) => selected.id === item.id));
      state[proposal].selected = [];
    },
    selectDfeFromAvailable: (state: NewContractProposalState, action: { payload: { ids: number[]; dfeType: EDfeType } }) => {
      const { ids, dfeType } = action.payload;
      const { available } = dfePropertyMap[dfeType];

      state[available].selected = state[available].items.filter(({ id }) => ids.some((itemId) => id === itemId));
    },
    selectDfeFromProposal: (state: NewContractProposalState, action: { payload: { ids: number[]; dfeType: EDfeType } }) => {
      const { ids, dfeType } = action.payload;
      const { proposal } = dfePropertyMap[dfeType];

      state[proposal].selected = state[proposal].items.filter(({ id }) => ids.some((itemId) => id === itemId));
    },
    removeDfeFromProposalById: (state: NewContractProposalState, action: { payload: { id: number; dfeType: EDfeType } }) => {
      const { id, dfeType } = action.payload;
      const { available, proposal } = dfePropertyMap[dfeType];

      state[proposal].selected = state[proposal].items.filter(({ id: itemId }) => itemId === id);
      state[available].items = [...state[available].items, ...state[proposal].selected];
      state[proposal].items = state[proposal].items.filter(({ id: itemId }) => itemId !== id);
      state[proposal].selected = [];
    },
    removeAllDfesFromProposal: (state: NewContractProposalState, action: { payload: { dfeType: EDfeType } }) => {
      const { dfeType } = action.payload;
      const { available, proposal } = dfePropertyMap[dfeType];

      state[available].items = [...state[available].items, ...state[proposal].items];
      state[proposal].items = [];
      state[proposal].selected = [];
    },
    dropFromProposalByIds: (state: NewContractProposalState, action: { payload: { ids: number[]; dfeType: EDfeType } }) => {
      const { ids, dfeType } = action.payload;
      const { proposal } = dfePropertyMap[dfeType];

      state[proposal].items = state[proposal].items.filter((item) => !ids.includes(item.id));
      state[proposal].selected = state[proposal].selected.filter((item) => !ids.includes(item.id));
    },
  },
});

export const {
  reset,
  setAvailableDfes,
  deleteDfes,
  addDfeToProposal,
  removeDfeFromProposal,
  selectDfeFromAvailable,
  selectDfeFromProposal,
  removeDfeFromProposalById,
  removeAllDfesFromProposal,
  dropFromProposalByIds,
} = contractSlice.actions;

// Helper action creators for easier usage
export const dfeActions = {
  setAvailable: (items: ICTe[], dfeType: EDfeType) => setAvailableDfes({ items, dfeType }),
  delete: (items: ICTe[], dfeType: EDfeType) => deleteDfes({ items, dfeType }),
  addToProposal: (dfeType: EDfeType) => addDfeToProposal({ dfeType }),
  removeFromProposal: (dfeType: EDfeType) => removeDfeFromProposal({ dfeType }),
  selectFromAvailable: (ids: number[], dfeType: EDfeType) => selectDfeFromAvailable({ ids, dfeType }),
  selectFromProposal: (ids: number[], dfeType: EDfeType) => selectDfeFromProposal({ ids, dfeType }),
  removeDfeFromProposalById: (id: number, dfeType: EDfeType) => removeDfeFromProposalById({ id, dfeType }),
  removeAllFromProposal: (dfeType: EDfeType) => removeAllDfesFromProposal({ dfeType }),
  dropFromProposalByIds: (ids: number[], dfeType: EDfeType) => dropFromProposalByIds({ ids, dfeType }),
};

export default contractSlice.reducer;
